import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  analyticsAPI,
  widgetsAPI,
  AnalyticsOverview,
  WidgetAnalytics,
  DailyAnalytics,
  Widget,
} from '../lib/api';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft,
  Loader2,
  Eye,
  MessageSquare,
  Users,
  Send,
  Globe,
  TrendingUp,
} from 'lucide-react';

export default function AnalyticsPage() {
  const { widgetId } = useParams<{ widgetId?: string }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [selectedWidgetId, setSelectedWidgetId] = useState(widgetId || '');

  // Data
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [widgetAnalytics, setWidgetAnalytics] = useState<WidgetAnalytics | null>(
    null
  );
  const [dailyData, setDailyData] = useState<DailyAnalytics[]>([]);

  useEffect(() => {
    loadWidgets();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [selectedWidgetId, days]);

  const loadWidgets = async () => {
    try {
      const data = await widgetsAPI.list();
      setWidgets(data.widgets);
    } catch (error) {
      console.error('Failed to load widgets:', error);
    }
  };

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      if (selectedWidgetId) {
        const [analytics, daily] = await Promise.all([
          analyticsAPI.widget(selectedWidgetId, days),
          analyticsAPI.daily(selectedWidgetId, days),
        ]);
        setWidgetAnalytics(analytics);
        setDailyData(daily.daily);
        setOverview(null);
      } else {
        const data = await analyticsAPI.overview(days);
        setOverview(data);
        setWidgetAnalytics(null);
        setDailyData([]);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (isLoading && !overview && !widgetAnalytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const stats = selectedWidgetId
    ? [
        {
          name: 'Page Views',
          value: widgetAnalytics?.totals.page_views || 0,
          icon: Eye,
          color: 'bg-blue-500',
        },
        {
          name: 'Widget Opens',
          value: widgetAnalytics?.totals.widget_opens || 0,
          icon: MessageSquare,
          color: 'bg-green-500',
        },
        {
          name: 'Messages Sent',
          value: widgetAnalytics?.totals.messages_sent || 0,
          icon: Send,
          color: 'bg-purple-500',
        },
        {
          name: 'Unique Sessions',
          value: widgetAnalytics?.totals.unique_sessions || 0,
          icon: Users,
          color: 'bg-orange-500',
        },
      ]
    : [
        {
          name: 'Page Views',
          value: overview?.totals.page_views || 0,
          icon: Eye,
          color: 'bg-blue-500',
        },
        {
          name: 'Widget Opens',
          value: overview?.totals.widget_opens || 0,
          icon: MessageSquare,
          color: 'bg-green-500',
        },
        {
          name: 'Messages Sent',
          value: overview?.totals.messages_sent || 0,
          icon: Send,
          color: 'bg-purple-500',
        },
        {
          name: 'Unique Sessions',
          value: overview?.totals.unique_sessions || 0,
          icon: Users,
          color: 'bg-orange-500',
        },
      ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          {widgetId && (
            <button
              onClick={() => navigate('/analytics')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              All Widgets
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600 mt-1">
            {selectedWidgetId
              ? `Analytics for ${widgetAnalytics?.widget.name || 'Widget'}`
              : 'Overview of all widgets'}
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Widget Selector */}
          <select
            value={selectedWidgetId}
            onChange={(e) => setSelectedWidgetId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
          >
            <option value="">All Widgets</option>
            {widgets.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>

          {/* Time Range Selector */}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-white rounded-xl border border-gray-200 p-6"
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}
              >
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stat.value.toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">{stat.name}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      {selectedWidgetId && dailyData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Messages Over Time */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Messages Over Time
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    labelFormatter={formatDate}
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="messages_sent"
                    stroke="#8b5cf6"
                    fill="#8b5cf6"
                    fillOpacity={0.2}
                    name="Messages"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Sessions Over Time */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Unique Sessions
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    labelFormatter={formatDate}
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="unique_sessions"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.2}
                    name="Sessions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Widget-specific details */}
      {selectedWidgetId && widgetAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Domains */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Top Domains
            </h3>
            {widgetAnalytics.by_domain.length === 0 ? (
              <p className="text-gray-500 text-sm">No data available</p>
            ) : (
              <div className="space-y-3">
                {widgetAnalytics.by_domain.slice(0, 5).map((domain) => {
                  const maxMessages =
                    widgetAnalytics.by_domain[0]?.messages_sent || 1;
                  const percentage = (domain.messages_sent / maxMessages) * 100;

                  return (
                    <div key={domain.domain}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-900 truncate">
                          {domain.domain}
                        </span>
                        <span className="text-gray-600">
                          {domain.messages_sent} messages
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top Countries */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Top Countries
            </h3>
            {widgetAnalytics.by_country.length === 0 ? (
              <p className="text-gray-500 text-sm">No data available</p>
            ) : (
              <div className="space-y-3">
                {widgetAnalytics.by_country.slice(0, 5).map((country) => {
                  const maxEvents =
                    widgetAnalytics.by_country[0]?.total_events || 1;
                  const percentage = (country.total_events / maxEvents) * 100;

                  return (
                    <div key={country.country}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-900">
                          {country.country || 'Unknown'}
                        </span>
                        <span className="text-gray-600">
                          {country.unique_sessions} sessions
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overview - Widgets Table */}
      {!selectedWidgetId && overview && overview.widgets.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Widgets Performance
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">
                    Widget
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">
                    Events
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">
                    Messages
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">
                    Sessions
                  </th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {overview.widgets.map((widget) => (
                  <tr key={widget.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">
                        {widget.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-gray-600">
                      {widget.total_events.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-600">
                      {widget.messages_sent.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-600">
                      {widget.unique_sessions.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setSelectedWidgetId(widget.id)}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        View Details →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading &&
        !selectedWidgetId &&
        (!overview || overview.totals.total_events === 0) && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No analytics data yet
            </h3>
            <p className="text-gray-600">
              Analytics will appear here once your widgets start receiving
              traffic.
            </p>
          </div>
        )}
    </div>
  );
}
